import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  FaTrashAlt,
  FaArrowLeft,
  FaShieldAlt,
  FaTruck,
  FaUndo,
  FaLock,
  FaShoppingBag,
  FaArrowRight,
  FaMinus,
  FaPlus,
  FaCheckCircle,
  FaBoxOpen,
} from "react-icons/fa";

import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./cart.css";

// ✅ CRITICAL: Fallback URL includes /api 
const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const pageVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: "easeOut" } },
  exit: { opacity: 0, x: -35, scale: 0.97, transition: { duration: 0.25 } },
};

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingItems, setUpdatingItems] = useState([]);
  const [removingItems, setRemovingItems] = useState([]);
  const { pathname } = useLocation();

  const generateItemId = (item) =>
    `${item.productId}_${item.color || "none"}_${item.band || "none"}_${item.size || "none"}_${item.storage || "none"}_${item.memory || "none"}_${item.processor || "none"}_${item.connectivity || "none"}`;

  useEffect(() => {
    const loadCart = async () => {
      try {
        const guestId = localStorage.getItem("guestId");
        const token = localStorage.getItem("token");

        // ✅ If no token and no guestId, user has empty cart
        if (!guestId && !token) {
          setCartItems([]);
          setLoading(false);
          return;
        }

        // ✅ Send token if available, else guestId
        const url = token 
          ? `${REACT_APP_API_URL}/cart` 
          : `${REACT_APP_API_URL}/cart?guestId=${encodeURIComponent(guestId)}`;

        const response = await fetch(url, {
          headers: { 
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }) // ✅ Send token
          },
        });

        if (!response.ok) throw new Error("Unable to load cart");

        const data = await response.json();

        const items = (data.items || []).map((item) => {
          const variantParts = [
            item.color, item.band, item.size, item.storage, item.memory, item.processor, item.connectivity,
          ].filter(Boolean);

          return {
            id: generateItemId(item),
            productId: item.productId,
            name: item.name,
            price: Number(item.price) || 0,
            originalPrice: Number(item.originalPrice) || Number(item.price) || 0,
            qty: Math.max(1, Number(item.quantity) || 1),
            image: item.image,
            color: item.color || "",
            band: item.band || "",
            memory: item.memory || "",
            storage: item.storage || "",
            size: item.size || "",
            connectivity: item.connectivity || "",
            processor: item.processor || "",
            sku: item.sku || "",
            variant: variantParts.join(" · "),
          };
        });

        setCartItems(items);
      } catch (error) {
        console.error("Cart load failed:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  const fmt = (amount) =>
    "₹" + Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const updateQty = async (id, delta) => {
    const item = cartItems.find((product) => product.id === id);
    if (!item || updatingItems.includes(id) || removingItems.includes(id)) return;
    if (item.qty + delta < 1) return;

    const previousQty = item.qty;
    const newQty = previousQty + delta;
    const guestId = localStorage.getItem("guestId");
    const token = localStorage.getItem("token");

    setUpdatingItems((prev) => [...prev, id]);
    setCartItems((prev) => prev.map((product) => product.id === id ? { ...product, qty: newQty } : product));

    try {
      const response = await fetch(`${REACT_APP_API_URL}/cart/add`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }) 
        },
        body: JSON.stringify({
          guestId,
          product: {
            productId: item.productId, name: item.name, price: item.price, quantity: newQty,
            image: item.image, color: item.color, band: item.band, memory: item.memory,
            storage: item.storage, size: item.size, connectivity: item.connectivity,
            processor: item.processor, sku: item.sku,
          },
        }),
      });

      if (!response.ok) throw new Error("Failed to update cart quantity");
    } catch (error) {
      console.error("Quantity update failed:", error);
      setCartItems((prev) => prev.map((product) => product.id === id ? { ...product, qty: previousQty } : product));
    } finally {
      setUpdatingItems((prev) => prev.filter((itemId) => itemId !== id));
    }
  };

  const removeItem = async (item) => {
    if (removingItems.includes(item.id) || updatingItems.includes(item.id)) return;

    const guestId = localStorage.getItem("guestId");
    const token = localStorage.getItem("token");

    setRemovingItems((prev) => [...prev, item.id]);
    setCartItems((prev) => prev.filter((product) => product.id !== item.id));

    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/cart/remove/${encodeURIComponent(item.productId)}?guestId=${encodeURIComponent(guestId || "")}`,
        {
          method: "DELETE",
          headers: { 
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({
            color: item.color, band: item.band, memory: item.memory, storage: item.storage,
            size: item.size, connectivity: item.connectivity, processor: item.processor,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to remove cart item");
    } catch (error) {
      console.error("Remove item failed:", error);
      window.location.reload(); 
    } finally {
      setRemovingItems((prev) => prev.filter((itemId) => itemId !== item.id));
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const totalOriginal = cartItems.reduce((sum, item) => sum + item.originalPrice * item.qty, 0);
  const discount = Math.max(0, totalOriginal - subtotal);
  const shipping = subtotal === 0 || subtotal > 50000 ? 0 : 499;
  const total = subtotal + shipping;
  const totalItems = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const freeShippingRemaining = Math.max(0, 50000 - subtotal);

  if (loading) {
    return (
      <>
        <Header />
        <main className="cart-page cart-loading">
          <Container>
            <div className="cart-loading-card">
              <span className="cart-loader" />
              <h5>Preparing your cart</h5>
              <p>Getting your selected products ready...</p>
            </div>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  if (cartItems.length === 0) {
    return (
      <>
        <Header />
        <main className="cart-page">
          <Container>
            <motion.div className="empty-cart-wrap" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="empty-cart-illustration">
                <div className="empty-cart-circle"><FaShoppingBag /></div>
                <span className="empty-cart-spark spark-one">✦</span>
                <span className="empty-cart-spark spark-two">✧</span>
              </div>
              <span className="cart-eyebrow">YOUR SHOPPING BAG</span>
              <h2>Your cart is waiting for something special.</h2>
              <p>Looks like you haven't added anything yet. Discover products you'll love and find your next favourite.</p>
              <Link to="/shop" className="shop-now-btn">Explore Collection <FaArrowRight /></Link>
              <div className="empty-cart-benefits">
                <span><FaShieldAlt /> Secure checkout</span>
                <span><FaTruck /> Reliable delivery</span>
                <span><FaUndo /> Easy returns</span>
              </div>
            </motion.div>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <motion.main className="cart-page" variants={pageVariants} initial="hidden" animate="show">
        <Container className="cart-container">
          <motion.div className="cart-topbar" variants={fadeUp}>
            <div className="cart-breadcrumb">
              <Link to="/shop">Shop</Link><span>/</span><span>Shopping Bag</span>
            </div>
            <div className="cart-secure-note"><FaLock /> Secure shopping experience</div>
          </motion.div>

          <motion.div className="cart-heading" variants={fadeUp}>
            <div>
              <span className="cart-eyebrow">YOUR SELECTION</span>
              <h1 className="cart-title">Shopping Bag<span className="cart-count">{totalItems}</span></h1>
              <p className="cart-subtitle">Review your selections before placing your order.</p>
            </div>
            <Link to="/shop" className="continue-shopping"><FaArrowLeft /><span>Continue shopping</span></Link>
          </motion.div>

          <Row className="cart-layout">
            <Col lg={8}>
              <motion.section variants={fadeUp}>
                <div className="cart-list-header">
                  <div>
                    <h2>Your items</h2>
                    <p>{totalItems} {totalItems === 1 ? "product" : "products"} selected</p>
                  </div>
                  <span className="cart-list-secure"><FaCheckCircle /> Ready for checkout</span>
                </div>

                <AnimatePresence mode="popLayout">
                  {cartItems.map((item) => (
                    <motion.article key={item.id} className="cart-item-card" variants={itemVariants} layout initial="hidden" animate="show" exit="exit">
                      <div className="cart-product-image">
                        <img src={item.image || "/placeholder.jpg"} alt={item.name} loading="lazy" onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = "/placeholder.jpg"; }} />
                      </div>
                      <div className="cart-product-info">
                        <span className="cart-product-label">{item.sku ? `SKU: ${item.sku}` : "SELECTED PRODUCT"}</span>
                        <h3 className="cart-item-name">{item.name}</h3>
                        {item.variant && <p className="cart-item-variant">{item.variant}</p>}
                        <div className="cart-unit-price">
                          <span>{fmt(item.price)}</span>
                          {item.originalPrice > item.price && <span className="cart-item-original">{fmt(item.originalPrice)}</span>}
                        </div>
                        <button type="button" className="remove-btn" onClick={() => removeItem(item)} disabled={updatingItems.includes(item.id) || removingItems.includes(item.id)} aria-label={`Remove ${item.name} from cart`}>
                          <FaTrashAlt /><span>{removingItems.includes(item.id) ? "Removing..." : "Remove"}</span>
                        </button>
                      </div>
                      <div className="cart-product-actions">
                        <span className="cart-quantity-label">Quantity</span>
                        <div className={`qty-stepper ${updatingItems.includes(item.id) ? "is-updating" : ""}`}>
                          <button type="button" onClick={() => updateQty(item.id, -1)} disabled={item.qty <= 1 || updatingItems.includes(item.id) || removingItems.includes(item.id)} aria-label="Decrease quantity"><FaMinus /></button>
                          <span aria-live="polite">{item.qty}</span>
                          <button type="button" onClick={() => updateQty(item.id, 1)} disabled={updatingItems.includes(item.id) || removingItems.includes(item.id)} aria-label="Increase quantity"><FaPlus /></button>
                        </div>
                        <div className="cart-line-total">
                          <span className="cart-line-total-label">Item total</span>
                          <strong>{fmt(item.price * item.qty)}</strong>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>

                <motion.div className="cart-delivery-note" variants={fadeUp}>
                  <div className="delivery-note-icon"><FaTruck /></div>
                  <div>
                    <h3>{shipping === 0 ? "You've unlocked free shipping!" : "You're close to free shipping"}</h3>
                    <p>{shipping === 0 ? "Your order qualifies for free delivery." : `Add ${fmt(freeShippingRemaining)} more to unlock free shipping.`}</p>
                    <div className="shipping-progress">
                      <span style={{ width: `${Math.min(100, (subtotal / 50000) * 100)}%` }} />
                    </div>
                  </div>
                  {shipping === 0 && <FaCheckCircle className="delivery-check" />}
                </motion.div>
              </motion.section>
            </Col>

            <Col lg={4}>
              <motion.aside className="order-summary" variants={fadeUp}>
                <div className="summary-header">
                  <div>
                    <span className="cart-eyebrow">YOUR ORDER</span>
                    <h2 className="summary-title">Order summary</h2>
                  </div>
                  <div className="summary-bag-icon"><FaShoppingBag /></div>
                </div>

                <div className="summary-item-count">
                  <FaBoxOpen /><span>{totalItems} {totalItems === 1 ? "item" : "items"} in your bag</span>
                </div>

                <div className="summary-row"><span className="label">Subtotal</span><span className="value">{fmt(subtotal)}</span></div>
                {discount > 0 && <div className="summary-row"><span className="label">Product savings</span><span className="value discount">− {fmt(discount)}</span></div>}
                <div className="summary-row"><span className="label">Shipping</span><span className={`value ${shipping === 0 ? "shipping-free" : ""}`}>{shipping === 0 ? "FREE" : fmt(shipping)}</span></div>
                <div className="summary-shipping-hint"><FaTruck />{shipping === 0 ? "Free shipping applied" : `Free shipping over ${fmt(50000)}`}</div>
                <hr className="summary-divider" />

                <div className="summary-total">
                  <div>
                    <span>Total amount</span>
                    <small>Inclusive of displayed prices</small>
                  </div>
                  <strong>{fmt(total)}</strong>
                </div>

                {discount > 0 && <div className="summary-savings"><FaCheckCircle />You're saving {fmt(discount)} on your order</div>}

                <Link to="/checkout" className="checkout-link">
                  <span>Proceed to checkout</span><FaArrowRight />
                </Link>

                <div className="summary-payment-note"><FaLock /><span>Your checkout is protected and secure.</span></div>
                <div className="trust-badges">
                  <div><FaShieldAlt /><span>Secure<br />payments</span></div>
                  <div><FaTruck /><span>Reliable<br />delivery</span></div>
                  <div><FaUndo /><span>Easy<br />returns</span></div>
                </div>
              </motion.aside>

              <motion.div className="cart-help-card" variants={fadeUp}>
                <div className="cart-help-icon"><FaShieldAlt /></div>
                <div>
                  <h3>Shop with confidence</h3>
                  <p>Review your order details before continuing to checkout.</p>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.main>
      <Footer />
    </>
  );
};

export default Cart;
